import Toast from 'react-native-toast-message'
import ErrorHandler from '../utils/ErrorHandler';
import { getCurrentOrgID } from './organization';
import findAssociativeChanges from '../utils/findAssociativeChanges';
import { removeStorageFile } from '../utils/FileManager';
import { supabase } from '../data/client';

const getRootResources = async () => {
    const orgID = await getCurrentOrgID();
    const {error, data} = await supabase.from('Folders').select('id').eq('name', 'ROOT').eq('organizationID', orgID).is('parentFolderID', null).maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {query: "getRootResources", error}, `Could not get resources`)
        return null;
    }

    const folder = await getFolder(data.id)
    const files = await getRootFiles();

    return {...folder, name: "All Files", files: files};
}

// const getRootFolders = async () => {
//     const orgID = await getCurrentOrgID();
//     const {error, data} = await supabase.rpc('get_folders_with_created_by', {organizationid: orgID});

//     if(error){
//         ErrorHandler(Error(error.message), {query: "getRootFolders", error}, `Something went wrong fetching the folders`)
//         return [];
//     }

//     return data;
// }


const getRootFiles = async () => {
    const orgID = await getCurrentOrgID();
    const {error, data} = await supabase.from('Files').select('*, uploadedBy:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)').eq('organizationID', orgID).is('folderID', null);

    if(error){
        ErrorHandler(Error(error.message), {query: "getRootFiles", error}, `Something went wrong fetching the files`)
        return [];
    }

    return data;
}


const getFolder = async (id) => {
    const {error, data} = await supabase.from('Folders')
        .select('*, Roles(*), files:Files(*, uploadedBy:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)), subfolders:Folders(*, Roles(*))')
        .eq('id', id)
        .order('name', {referencedTable: 'Folders', ascending: true})
        .order('filename', {referencedTable: 'Files', ascending: true})
        .maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {query: "getFolder", id, error}, `Something went wrong fetching the folder`)
        return null;
    }

    return data;
}

const saveFolder = async (details) => {
    const orgID = await getCurrentOrgID();

    const newFolder = {
        id: details.id,
        organizationID: orgID,
        name: details.name,
        parentFolderID: details.parentFolderID
    }

    if(details.id){
        return updateFolder(newFolder, details.Roles);
    }
    else{
        return createFolder({...newFolder, createdByID: details.createdByID}, details.Roles, details.noMessage)
    }
}

const createFolder = async (newFolder, Roles = [], noMessage) => {
    const {insertError} = await supabase.from('Folders').insert(newFolder);
    const {error, data} = await supabase.from('Folders').select('*, Roles(*), files:Files(*, uploadedBy:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)), subfolders:Folders(*, Roles(*))').limit(1).order('createdAt', {ascending: false}).maybeSingle();
    let newRoles = [];

    if(error || insertError){
        ErrorHandler(Error(error.message || insertError.message), {mutation: "createFolder", newFolder, error, insertError}, `Something went wrong creating that folder`)
        return null;
    }

    if(Roles && Roles.length > 0){
        const {error: roleError, data: roleData} = await supabase.from('FolderRoles').insert(Roles?.map(r => ({organizationID: newFolder.organizationID, folderID: data.id, roleID: r.id})).flat()).select();
        newRoles = roleData

        if(roleError){
            ErrorHandler(Error(roleError.message), {mutation: "createFolder", Roles, error}, `We created the Folder, but something went wrong creating the roles for it`);
            return null;
        }
    }
       
    if(!noMessage){
        Toast.show({ type: 'success', text2: `New folder has been added successfully!`});
    }
    
    return {...data, Roles: newRoles};
}


const updateFolder = async (input, Roles = []) => {
    const {error, data} = await supabase.from('Folders').update(input).eq('id', input.id).select('*, Roles(*)').single();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "updateFolder", input, error}, `Something went wrong updating that folder`)
        return null;
    }

    const { itemsToAdd, itemsToDelete, unchangedItems } = findAssociativeChanges(Roles, data.Roles);


    if(itemsToAdd && itemsToAdd.length > 0){
        const {error: roleError, data: roleData} = await supabase.from('FolderRoles').insert(itemsToAdd.map(r => ({organizationID: input.organizationID, folderID: input.id, roleID: r.id})).flat()).select();
    
        if(roleError){
            ErrorHandler(Error(roleError.message), {mutation: "updateFolder", input, error}, `We updated the Folder, but something went wrong creating the new roles for it`);
            return null;
        }
    }
    

    if(itemsToDelete && itemsToDelete.length > 0){
        const {error: deleteError} = await supabase.from('FolderRoles').delete().in('id', itemsToDelete.map(r => r.id));

        if(deleteError){
            ErrorHandler(Error(deleteError.message), {mutation: "updateFolder", input, error}, `We updated the Folder, but something went wrong deleting the roles for it`);
            return null;
        }
    }

    Toast.show({ type: 'success', text2: `Folder has been updated successfully!`});
    return {...data, Roles: [...unchangedItems, ...itemsToAdd]};
}


const deleteFolder = async ({folderID}) => {
    const runDeletes = async (id, first = false) => {
        const {error, data} = await supabase.from('Folders').select('*, files:Files(*), subfolders:Folders(*)').eq('id', id).maybeSingle();
        const {deleteError} = await supabase.from('Folders').delete().eq('id', id);

        if(deleteError){
            ErrorHandler(Error(deleteError.message), {mutation: "deleteFolder", folderID: id, error}, `Something went wrong deleting that folder`)
            throw Error();
        }

        data.files?.forEach(async f => {
            await deleteFile({fileID: f.id, key: f.key, noMessage: true});
            await removeStorageFile(f.key);
        })

        data.subfolders?.forEach(async f => {
            await runDeletes(f.id)
        })

        if(first){
            Toast.show({type: "success", text2: `The folder "${data.name}" was shredded successfully`})
        }
    }

    await runDeletes(folderID, true);
}


const deleteFile = async ({fileID, key, noMessage = false}) => {
    const {error: deleteError} = await supabase.from('Files').delete().eq('id', fileID);

    if(deleteError){
        ErrorHandler(Error(deleteError.message), {mutation: "deleteFile", fileID, deleteError}, `Something went wrong deleting that file`)
        throw Error();
    }

    if(!noMessage){
        Toast.show({
            type: 'success',
            text2: `File Successfully deleted`
        });
    }
}


const saveFiles = async ({files = [], path, folderID, uploadedByID}) => {
    const orgID = await getCurrentOrgID();
    
    const {error, data} = await supabase.from('Files').insert(files?.map(f => ({
        filename: (f.extension && !f.name.trim().endsWith(f.extension)) ? (f.name.trim() + f.extension) : f.name.trim(), 
        key: (f.extension && !f.name.trim().endsWith(f.extension)) ? (path + f.name.trim() + f.extension) : (path + f.name.trim()), 
        size: f.size, 
        folderID: !folderID || folderID == 'home' ? null : folderID, 
        uploadedByID: uploadedByID, 
        organizationID: orgID
    })).flat()).select();
    
    if(error){
        ErrorHandler(Error(error.message), {mutation: "saveFiles", files, path, folderID, uploadedByID, orgID, error}, `Something went wrong saving the files`)
        return [];
    }

    return data
}


const updateFile = async ({id, newFilename}) => {
    const {error, data} = await supabase.from('Files').update({filename: newFilename}).eq('id', id).select();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "updateFile", id, newFilename, error}, `Something went wrong updating the file`)
        return [];
    }

    Toast.show({type:'success', text2:"Filename was updated successfully!"})
    return data;
}

const getLinks = async () => {
    const orgID = await getCurrentOrgID();
    const {data, error} = await supabase.from('Links').select('*').eq('organizationID', orgID).order('name', {ascending: true});
    if(error){
        ErrorHandler(Error(error.message), {mutation: "getLinks", error}, `Something went wrong getting the links`)
        return [];
    }
    return data
}

const getLink = async (id) => {
    const orgID = await getCurrentOrgID();
    const {data, error} = await supabase.from('Links').select('*').eq('organizationID', orgID).eq('id', id).maybeSingle();
    if(error){
        ErrorHandler(Error(error.message), {mutation: "getLink", id, error}, `Something went wrong getting the link`)
        return null;
    }
    return data
}

const saveLink = async ({details}) => {
    const orgID = await getCurrentOrgID();

    const link = {
        id: details.id,
        name: details.name?.trim(), 
        url: details.url?.trim(), 
        description: details.description?.trim(),
        expiration: details.expiration, 
        organizationID: orgID,
        createdByID: details.createdByID
    }

    if(details.id){
        return updateLink(link);
    }
    else{
        return createLink(link);
    }
}


const createLink = async (link) => {
    const {error, data} = await supabase.from('Links').insert(link).select();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "saveLink", link, error}, `Something went wrong saving the link`)
        throw Error
    }

    return data
}


const updateLink = async (link) => {
    const {error, data} = await supabase.from('Links').update(link).eq('id', link.id).select();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "updateLink", link, error}, `Something went wrong updating the link`)
        throw Error
    }

    return data
}

const deleteLink = async ({id}) => {
    const {error} = await supabase.from('Links').delete().eq('id', id);
    if(error){
        ErrorHandler(Error(error.message), {mutation: "delateLink", id, error}, `Something went wrong deleting the link`)
        throw Error
    }
}

export { 
    getRootResources,
    getFolder,
    // getFiles,
    deleteFile,
    saveFolder,
    deleteFolder,
    saveFiles,
    updateFile,
    getLinks,
    getLink,
    saveLink,
    deleteLink
}